/**
 * TAGP Frontend Configuration
 *
 * Centralized configuration loaded from environment variables.
 *
 * ## Environment Files
 *
 * Configuration is loaded from `config/` directory based on build mode:
 *
 * | Command              | Mode        | Env File Loaded           |
 * |----------------------|-------------|---------------------------|
 * | `npm run dev`        | development | config/env.development    |
 * | `npm run build`      | production  | config/env.production     |
 * | `npm run build:staging` | staging  | config/env.staging        |
 *
 * ## Usage
 *
 * ```typescript
 * import { config } from '@/config';
 *
 * // Access configuration
 * const apiUrl = config.api.baseUrl;
 * const isDebug = config.features.debug;
 * ```
 *
 * ## Runtime Overrides
 *
 * For deployments needing runtime config without rebuilding,
 * set `window.__TAGP_CONFIG__` before the app loads.
 */

// =============================================================================
// Type Definitions
// =============================================================================

export interface ApiConfig {
  /** TAGP Backend API base URL */
  baseUrl: string
  /** Agentserve Backend base URL */
  agentserveUrl: string
  /** AIWorkflows Backend base URL */
  aiworkflowsUrl: string
  /** AIServices base URL */
  aiservicesUrl: string
  /** Enterprise Ontology service base URL */
  enterpriseOntologyUrl: string
  /** Notification service base URL */
  notificationsUrl: string
  /** MonitoringService base URL (nginx prefix; REST lives under `/v1`) */
  monitoringUrl: string
  /** Voice Gateway URL for WebRTC/WS voice connections */
  voiceGatewayUrl: string
  /** Request timeout in milliseconds */
  timeout: number
  /** Number of retry attempts */
  retryAttempts: number
  /** Initial retry delay in milliseconds */
  retryDelay: number
}

export interface AppConfig {
  /** Application name */
  name: string
  /** Application version */
  version: string
  /** Application description */
  description: string
}

export interface EnvConfig {
  /** Current build mode */
  mode: string
  /** Is development mode */
  isDevelopment: boolean
  /** Is production mode */
  isProduction: boolean
  /** Is staging mode */
  isStaging: boolean
}

export interface FeatureConfig {
  /** Enable debug mode */
  debug: boolean
  /** Enable analytics */
  analytics: boolean
  /** Enable performance monitoring */
  performanceMonitoring: boolean
  /** Force Ideate into demo mode (simulated timers, no real API) */
  ideateDemoMode?: boolean
}

export interface AuthConfig {
  /** Access token storage key */
  tokenKey: string
  /** Refresh token storage key */
  refreshTokenKey: string
  /** Token refresh threshold in seconds */
  refreshThreshold: number
  /**
   * Auto-logout after this many minutes without user interaction (0 = disabled).
   * Overridden by backend /auth/config when present.
   */
  sessionInactivityTimeoutMinutes: number
}

export interface StorageConfig {
  /** Theme ID storage key */
  theme: string
  /** Theme mode storage key */
  themeMode: string
  /** Language storage key */
  language: string
}

export interface ServicesConfig {
  /** Sentry DSN */
  sentryDsn: string | undefined
  /** Analytics ID */
  analyticsId: string | undefined
  /** PostHog project API key (phc_…). Empty disables the SDK. */
  posthogKey: string | undefined
  /** PostHog ingest + UI host (no trailing slash). */
  posthogHost: string
  /** Cluster name stamped on every PostHog event as `k8s_cluster_name`. */
  k8sClusterName: string
}

export interface Config {
  api: ApiConfig
  app: AppConfig
  env: EnvConfig
  features: FeatureConfig
  auth: AuthConfig
  storage: StorageConfig
  services: ServicesConfig
}

// =============================================================================
// Runtime Config Support
// =============================================================================

interface RuntimeConfig {
  api?: Partial<Pick<ApiConfig, 'baseUrl' | 'agentserveUrl' | 'aiworkflowsUrl' | 'aiservicesUrl' | 'enterpriseOntologyUrl' | 'notificationsUrl' | 'monitoringUrl' | 'voiceGatewayUrl'>>
  features?: Partial<FeatureConfig>
}

declare global {
  interface Window {
    __TAGP_CONFIG__?: RuntimeConfig
  }
}

function getRuntimeConfig(): RuntimeConfig {
  if (typeof window !== 'undefined' && window.__TAGP_CONFIG__) {
    return window.__TAGP_CONFIG__
  }
  return {}
}

// =============================================================================
// Environment Variable Helpers
// =============================================================================

// Vite statically replaces import.meta.env.VITE_* at build time using literal
// property access only. Dynamic bracket access (import.meta.env[key]) evaluates
// to undefined in production bundles because no runtime dictionary is emitted.
// ENV_MAP forces static analysis of every key so values are correctly inlined.
// Any new VITE_* variable used via env() must be added here.
const ENV_MAP = {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_AGENTSERVE_URL: import.meta.env.VITE_AGENTSERVE_URL,
  VITE_AIWORKFLOWS_URL: import.meta.env.VITE_AIWORKFLOWS_URL,
  VITE_AISERVICES_URL: import.meta.env.VITE_AISERVICES_URL,
  VITE_ENTERPRISE_ONTOLOGY_URL: import.meta.env.VITE_ENTERPRISE_ONTOLOGY_URL,
  VITE_NOTIFICATIONS_URL: import.meta.env.VITE_NOTIFICATIONS_URL,
  VITE_MONITORING_URL: import.meta.env.VITE_MONITORING_URL,
  VITE_VOICE_GATEWAY_URL: import.meta.env.VITE_VOICE_GATEWAY_URL,
  VITE_API_TIMEOUT: import.meta.env.VITE_API_TIMEOUT,
  VITE_API_RETRY_ATTEMPTS: import.meta.env.VITE_API_RETRY_ATTEMPTS,
  VITE_API_RETRY_DELAY: import.meta.env.VITE_API_RETRY_DELAY,
  VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
  VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
  VITE_APP_DESCRIPTION: import.meta.env.VITE_APP_DESCRIPTION,
  VITE_ENABLE_DEBUG: import.meta.env.VITE_ENABLE_DEBUG,
  VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
  VITE_ENABLE_PERFORMANCE_MONITORING: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING,
  VITE_IDEATE_DEMO_MODE: import.meta.env.VITE_IDEATE_DEMO_MODE,
  VITE_TOKEN_REFRESH_THRESHOLD: import.meta.env.VITE_TOKEN_REFRESH_THRESHOLD,
  VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES: import.meta.env.VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES,
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  VITE_ANALYTICS_ID: import.meta.env.VITE_ANALYTICS_ID,
  VITE_POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY,
  VITE_POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST,
  VITE_K8S_CLUSTER_NAME: import.meta.env.VITE_K8S_CLUSTER_NAME,
} as const

type EnvKey = keyof typeof ENV_MAP

function env(key: EnvKey, defaultValue: string = ''): string {
  const value = ENV_MAP[key]
  return value !== undefined && value !== '' ? String(value) : defaultValue
}

function envBool(key: EnvKey, defaultValue: boolean = false): boolean {
  const value = env(key, String(defaultValue))
  return value === 'true' || value === '1'
}

function envNumber(key: EnvKey, defaultValue: number): number {
  const value = env(key, String(defaultValue))
  const parsed = Number.parseInt(value, 10)
  return isNaN(parsed) ? defaultValue : parsed
}

// =============================================================================
// Build Configuration
// =============================================================================

const runtime = getRuntimeConfig()
const mode = import.meta.env.MODE || 'development'

/**
 * Application configuration singleton
 */
export const config: Config = {
  // API Configuration
  // Fallbacks use relative paths to work through nginx gateway
  api: {
    baseUrl: runtime.api?.baseUrl || env('VITE_API_BASE_URL', '/api'),
    agentserveUrl: runtime.api?.agentserveUrl || env('VITE_AGENTSERVE_URL', '/agentserve'),
    aiworkflowsUrl: runtime.api?.aiworkflowsUrl || env('VITE_AIWORKFLOWS_URL', '/aiworkflows'),
    aiservicesUrl: runtime.api?.aiservicesUrl || env('VITE_AISERVICES_URL', '/aiservices'),
    enterpriseOntologyUrl: runtime.api?.enterpriseOntologyUrl || env('VITE_ENTERPRISE_ONTOLOGY_URL', '/enterprise-ontology'),
    notificationsUrl: runtime.api?.notificationsUrl || env('VITE_NOTIFICATIONS_URL', '/notifications'),
    monitoringUrl: runtime.api?.monitoringUrl || env('VITE_MONITORING_URL', '/monitoring-api'),
    voiceGatewayUrl: runtime.api?.voiceGatewayUrl || env('VITE_VOICE_GATEWAY_URL', '/voice-gateway'),
    timeout: envNumber('VITE_API_TIMEOUT', 30000),
    retryAttempts: envNumber('VITE_API_RETRY_ATTEMPTS', 3),
    retryDelay: envNumber('VITE_API_RETRY_DELAY', 1000),
  },

  // Application Metadata
  app: {
    name: env('VITE_APP_NAME', 'AQUA'),
    version: env('VITE_APP_VERSION', '1.0.0'),
    description: env('VITE_APP_DESCRIPTION', 'The Agent Governance Platform'),
  },

  // Environment Info
  env: {
    mode,
    isDevelopment: import.meta.env.DEV === true,
    isProduction: import.meta.env.PROD === true,
    isStaging: mode === 'staging',
  },

  // Feature Flags
  features: {
    debug: runtime.features?.debug ?? envBool('VITE_ENABLE_DEBUG', import.meta.env.DEV),
    analytics: runtime.features?.analytics ?? envBool('VITE_ENABLE_ANALYTICS', false),
    performanceMonitoring:
      runtime.features?.performanceMonitoring ??
      envBool('VITE_ENABLE_PERFORMANCE_MONITORING', false),
    ideateDemoMode: envBool('VITE_IDEATE_DEMO_MODE', false),
  },

  // Authentication Keys
  auth: {
    tokenKey: 'tagp-token',
    refreshTokenKey: 'tagp-refresh-token',
    refreshThreshold: envNumber('VITE_TOKEN_REFRESH_THRESHOLD', 300),
    sessionInactivityTimeoutMinutes: envNumber('VITE_SESSION_INACTIVITY_TIMEOUT_MINUTES', 1440),
  },

  // Storage Keys
  storage: {
    theme: 'tagp-theme-id',
    themeMode: 'tagp-theme-mode',
    language: 'tagp-language',
  },

  // External Services
  services: {
    sentryDsn: env('VITE_SENTRY_DSN') || undefined,
    analyticsId: env('VITE_ANALYTICS_ID') || undefined,
    posthogKey: env('VITE_POSTHOG_KEY') || env('VITE_ANALYTICS_ID') || undefined,
    posthogHost: env('VITE_POSTHOG_HOST', 'https://aqua-obs.tredence.com/posthog'),
    k8sClusterName: env('VITE_K8S_CLUSTER_NAME'),
  },
}

// =============================================================================
// Convenience Exports
// =============================================================================

/** TAGP Backend API base URL */
export const API_BASE_URL = config.api.baseUrl

/** Agentserve Backend base URL */
export const AGENTSERVE_URL = config.api.agentserveUrl

/** AIWorkflows Backend base URL */
export const AIWORKFLOWS_URL = config.api.aiworkflowsUrl

/** AIServices base URL */
export const AISERVICES_URL = config.api.aiservicesUrl

/** Is development mode */
export const IS_DEVELOPMENT = config.env.isDevelopment

/** Is production mode */
export const IS_PRODUCTION = config.env.isProduction

// =============================================================================
// Validation (startup logging lives in ./startupLog: utils/logger imports this module)
// =============================================================================

export function validateConfig(): string[] {
  const errors: string[] = []

  if (!config.api.baseUrl) {
    errors.push('VITE_API_BASE_URL is not configured')
  }

  if (!config.api.agentserveUrl) {
    errors.push('VITE_AGENTSERVE_URL is not configured')
  }

  if (!config.api.aiworkflowsUrl) {
    errors.push('VITE_AIWORKFLOWS_URL is not configured')
  }

  return errors
}

export default config
